/**
 * TenantSwitchDialog
 *
 * Centered modal dialog that lists every tenant the user has access to,
 * filterable by free-text search. Replaces the previous inline tenant
 * list inside the avatar menu, which didn't scale past ~10 entries.
 *
 * UX shape:
 *  - Search input is auto-focused on open.
 *  - The currently-signed-in tenant is pinned at the top with a "Current"
 *    badge and isn't clickable (no-op if clicked).
 *  - All other tenants render as buttons; click-to-switch immediately
 *    closes the dialog and triggers `setSelectedTenant` plus a broad
 *    React Query cache wipe that drops every cached query except the
 *    user-scoped tenants list (preserved so the active-tenant substring
 *    in the header stays visible across the switch). This is intentionally
 *    aggressive: any query that may contain tenant-scoped data must not
 *    leak across tenants, and we don't currently tag queries by scope.
 *  - Keyboard: ↑/↓ moves the highlighted row, Enter switches, Esc closes.
 *  - Filter matches against displayName, defaultDomain, and tenantId.
 *
 * A11y: dialog role + aria-modal via Fluent's <Dialog>; the list uses
 * combobox/listbox + role="option" + aria-activedescendant so AT users
 * hear the highlighted row label as they arrow through.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Input,
  Spinner,
  Text,
  mergeClasses,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import type { AzureTenant } from '@/services/azureResourceService';
import { useAppStore } from '@/stores/appStore';
import { useTenants, azureResourceKeys } from '@/hooks/useAzureResources';
import { useTelemetry } from '@/hooks/useTelemetry';
import { tenantPrefix } from '@/routes';

interface TenantSwitchDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close (Esc, backdrop, cancel, or post-switch) */
  onClose: () => void;
  /** Fired after the user successfully switches tenants, immediately after
   *  `navigate()` has been called (the new route may not have finished
   *  rendering yet). Used by callers that opened the dialog on top of
   *  another surface (e.g. SettingsModal) to dismiss that surface too —
   *  `navigate()` only swaps the route Outlet, so wrapping surfaces don't
   *  unmount on their own. */
  onSwitched?: (tenantId: string) => void;
}

const useStyles = makeStyles({
  surface: {
    maxWidth: '480px',
    width: 'min(480px, 92vw)',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    minHeight: 0,
  },
  searchInput: {
    width: '100%',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    maxHeight: '320px',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBlock: tokens.spacingVerticalXS,
    // Scroll container with a single top divider; the bottom is owned by
    // DialogActions so we don't stack two near-identical lines.
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  currentSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  currentSectionLabel: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.02em',
  },
  currentSectionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingInline: tokens.spacingHorizontalM,
    paddingBlock: tokens.spacingVerticalS,
    // 4px horizontal inset so the row's rounded corners read against
    // the list container's edges (concentric radius — the rule is
    // outerRadius = innerRadius + padding, applied here in reverse:
    // we give the inner row breathing room so its 4px radius is visible).
    marginInline: tokens.spacingHorizontalXS,
    borderRadius: tokens.borderRadiusMedium,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    width: 'auto',
    color: tokens.colorNeutralForeground1,
    // Explicit transition property — never `transition: all`, per
    // make-interfaces-feel-better #14. Spec only transform + background
    // so unrelated style changes don't accidentally animate.
    transitionProperty: 'transform, background-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    // Subtle press feedback. 0.98 (not 0.96) because the row is wide —
    // a bigger scale would feel jumpy. Skill principle: scale-on-press
    // gives tactile confirmation the UI heard the click.
    ':active': {
      transform: 'scale(0.98)',
    },
    ':focus-visible': {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: '-2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
      ':active': { transform: 'none' },
    },
  },
  rowActive: {
    backgroundColor: tokens.colorNeutralBackground1Hover,
  },
  rowText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  rowName: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowDomain: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  empty: {
    paddingBlock: tokens.spacingVerticalL,
    paddingInline: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    paddingBlock: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
  },
  countHint: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    // Align with the Cancel button's vertical center. Without this the
    // Text baseline matches the button label baseline, which sits above
    // the button's optical center because the button has more padding
    // above the text than the surrounding Text element does.
    display: 'inline-flex',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
});

function matchesQuery(tenant: AzureTenant, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    tenant.displayName.toLowerCase().includes(q) ||
    tenant.defaultDomain.toLowerCase().includes(q) ||
    tenant.tenantId.toLowerCase().includes(q)
  );
}

export const TenantSwitchDialog = ({ open, onClose, onSwitched }: TenantSwitchDialogProps) => {
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedTenantId, setSelectedTenant } = useAppStore();
  const { data: tenants, isLoading } = useTenants();
  const { trackEvent } = useTelemetry();

  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  // Current tenant is pinned at the top of the dialog as a non-interactive
  // row. The "switchable" list excludes it so click-to-switch never lands
  // back on the same tenant. Within "switchable", we filter by query and
  // sort alphabetically by displayName (case-insensitive) for predictable
  // ordering — the API doesn't promise a stable order across requests.
  const currentTenant = useMemo(
    () => tenants?.find((t) => t.tenantId === selectedTenantId) ?? null,
    [tenants, selectedTenantId],
  );
  const switchable = useMemo(() => {
    if (!tenants) return [];
    return tenants
      .filter((t) => t.tenantId !== selectedTenantId)
      .filter((t) => matchesQuery(t, query))
      .sort((a, b) =>
        a.displayName.localeCompare(b.displayName, undefined, {
          sensitivity: 'base',
        }),
      );
  }, [tenants, selectedTenantId, query]);

  // Reset transient state on each *open transition* and emit one
  // telemetry event per open. We deliberately do NOT depend on
  // tenants?.length: if tenants resolve after the dialog opened (or
  // refetch while it's open), the previous version re-ran this effect,
  // wiping the user's typed query / activeIndex and re-emitting the
  // 'opened' event. A ref captures the latest count so the telemetry
  // payload is still accurate without re-running on tenant updates.
  const tenantCountRef = useRef(0);
  useEffect(() => {
    tenantCountRef.current = tenants?.length ?? 0;
  }, [tenants?.length]);
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActiveIndex(0);
    trackEvent('tenant.switch_dialog.opened', {
      tenantCount: String(tenantCountRef.current),
    });
  }, [open, trackEvent]);

  // Clamp the active index whenever the visible list shrinks (e.g. user
  // types and filters down) so it never points past the end. Also reset
  // to the top whenever the query changes — that's what users expect.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);
  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(switchable.length - 1, 0)));
  }, [switchable.length]);

  // Scroll the highlighted row into view as ↑/↓ moves it.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const node = listRef.current.querySelector<HTMLElement>(
      `[data-row-index="${activeIndex}"]`,
    );
    if (node && typeof node.scrollIntoView === 'function') {
      node.scrollIntoView({ block: 'nearest' });
    }
  }, [open, activeIndex]);

  const handleSwitch = useCallback(
    (tenantId: string) => {
      trackEvent('tenant.switch_dialog.switched', { tenantId });
      setSelectedTenant(tenantId);
      // Drop every cached query *except* the tenants list itself. Tenant-scoped
      // data (subscriptions, projects, etc.) must not leak across tenants,
      // but the tenants list itself is user-scoped — it's the same set of
      // tenants regardless of which one is currently active. Clearing it
      // would leave the header's active-tenant substring blank for several
      // seconds while ARM refetches, which reads as a bug to the user.
      // Use exact key equality (not prefix match) so any future tenant-scoped
      // queries nested under the tenants key (e.g. `[...tenants, id, details]`)
      // are still cleared on switch.
      const tenantsKey = azureResourceKeys.tenants();
      queryClient.removeQueries({
        predicate: (q) => {
          const key = q.queryKey;
          if (key.length !== tenantsKey.length) return true;
          for (let i = 0; i < tenantsKey.length; i++) {
            if (key[i] !== tenantsKey[i]) return true;
          }
          return false;
        },
      });
      // Navigate to the tenant's home (dashboard) — previous context
      // (project, app, workflow) is no longer valid in the new tenant.
      navigate(tenantPrefix(tenantId) || '/');
      onClose();
      onSwitched?.(tenantId);
    },
    [setSelectedTenant, queryClient, trackEvent, navigate, onClose, onSwitched],
  );

  const handleSearchKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (switchable.length === 0) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, switchable.length - 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (event.key === 'Home') {
        event.preventDefault();
        setActiveIndex(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        setActiveIndex(switchable.length - 1);
      } else if (event.key === 'Enter') {
        event.preventDefault();
        const target = switchable[activeIndex];
        if (target) handleSwitch(target.tenantId);
      }
    },
    [switchable, activeIndex, handleSwitch],
  );

  const activeDescendant =
    switchable[activeIndex] !== undefined
      ? `${listboxId}-${activeIndex}`
      : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Switch tenant</DialogTitle>
          <DialogContent className={styles.body}>
            <Input
              input={{
                ref: searchRef,
                role: 'combobox',
                'aria-controls': listboxId,
                'aria-expanded': true,
                'aria-autocomplete': 'list',
                ...(activeDescendant ? { 'aria-activedescendant': activeDescendant } : {}),
              }}
              autoFocus
              className={styles.searchInput}
              contentBefore={<SearchRegular />}
              placeholder="Search tenants…"
              value={query}
              onChange={(_e, data) => setQuery(data.value)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search tenants"
            />

            {/* Current-tenant disclosure rendered OUTSIDE the listbox.
                A non-interactive option inside role="listbox" is still
                announced as a selectable option by AT (the listbox
                pattern presumes every option is navigable). Lifting it
                out keeps the listbox semantics clean — only switchable
                tenants are options. */}
            {currentTenant && (
              <div
                className={styles.currentSection}
                aria-label="Currently signed in to tenant"
              >
                <span className={styles.currentSectionLabel}>Current</span>
                <div className={styles.currentSectionRow}>
                  <span className={styles.rowText}>
                    <span
                      className={styles.rowName}
                      title={currentTenant.displayName}
                    >
                      {currentTenant.displayName}
                    </span>
                    <span
                      className={styles.rowDomain}
                      title={currentTenant.defaultDomain}
                    >
                      {currentTenant.defaultDomain}
                    </span>
                  </span>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className={styles.loading} role="status" aria-label="Loading tenants">
                <Spinner size="tiny" />
                <span>Loading tenants…</span>
              </div>
            ) : (
              <div
                ref={listRef}
                className={styles.list}
                id={listboxId}
                role="listbox"
                aria-label="Switchable tenants"
              >
                {switchable.length === 0 ? (
                  <div className={styles.empty} role="status">
                    {query.trim()
                      ? `No tenants match "${query}".`
                      : 'No other tenants available.'}
                  </div>
                ) : (
                  switchable.map((tenant, index) => {
                    const isActive = index === activeIndex;
                    return (
                      <button
                        type="button"
                        key={tenant.tenantId}
                        id={`${listboxId}-${index}`}
                        data-row-index={index}
                        role="option"
                        aria-selected={isActive}
                        tabIndex={-1}
                        className={mergeClasses(
                          styles.row,
                          isActive && styles.rowActive,
                        )}
                        onClick={() => handleSwitch(tenant.tenantId)}
                        onMouseEnter={() => setActiveIndex(index)}
                      >
                        <span className={styles.rowText}>
                          <span
                            className={styles.rowName}
                            title={tenant.displayName}
                          >
                            {tenant.displayName}
                          </span>
                          <span
                            className={styles.rowDomain}
                            title={tenant.defaultDomain}
                          >
                            {tenant.defaultDomain}
                          </span>
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {tenants && tenants.length > 0 && (
              <Text className={styles.countHint}>
                {tenants.length} {tenants.length === 1 ? 'tenant' : 'tenants'}
              </Text>
            )}
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
